import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

export function useAuth(): AuthState & {
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInMicrosoft: (tillbakaTill: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({ user: null, session: null, loading: true });

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setState({ user: data.session?.user ?? null, session: data.session, loading: false });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setState({ user: session?.user ?? null, session, loading: false });
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? 'Fel e-post eller lösenord' : null };
  };

  /**
   * Inloggning med El-kretsens Microsoft-konton (Entra ID).
   *
   * Vilka konton som släpps in avgörs INTE här utan i Supabase: Azure-
   * leverantören pekar på El-kretsens tenant-URL, så ett Microsoft-konto från
   * någon annan organisation nekas redan hos Microsoft. Bara email och profile
   * begärs — namn och adress, ingen post, inga filer.
   */
  const signInMicrosoft = async (tillbakaTill: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile',
        // Alltid tillbaka till /login: ett nekat försök bär sitt fel i adressen,
        // och skickades det till en skyddad sida hade omdirigeringen tappat
        // det. Målet följer med i ?till= och LoginPage går dit när sessionen finns.
        redirectTo: `${window.location.origin}/login?till=${encodeURIComponent(tillbakaTill)}`,
      },
    });
    // Utan fel lämnar webbläsaren sidan för Microsofts inloggning.
    return { error: error ? 'Microsoft-inloggningen kunde inte startas. Försök igen om en stund.' : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return { ...state, signIn, signInMicrosoft, signOut };
}
